import { Injectable } from '@angular/core';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { BehaviorSubject } from 'rxjs';
import { RemoteActionService } from 'src/app/_services/remote-action/logic/remote-action.service';
import { AuthenticationService } from 'src/app/_services/authentication/authentication.service';
import { RemoteAction } from 'src/app/_model/remote-action/remote-action';
import { InitializeStatus } from 'src/app/_services/initialize/initialize-aware';
import { APILevel } from 'src/app/_services/sync/api-level';
import { TranslationsService } from '../translations.service';
import { TranslationDaoService } from '../../dao/translation-dao.service';
import { TranslateService } from '@ngx-translate/core';
import { setDefaultOptions } from 'date-fns';

@Injectable()
export class TranslationsLogicService implements TranslationsService {
  public initializeStatus$: BehaviorSubject<InitializeStatus> =
    new BehaviorSubject(InitializeStatus.NOT_INITIALIZED);

  // Lifecycle
  constructor(
    private logger: LoggingService,
    private translate: TranslateService,
    private remoteActionService: RemoteActionService,
    private authenticationService: AuthenticationService,
    private translationsDaoService: TranslationDaoService
  ) {
    this.initialize();
  }

  // Resetable
  public async reset() {
    await this.translationsDaoService.reset();
  }

  public async clear() {
    this.translationsDaoService.clear();
    for (const lang of this.translate.getLangs()) {
      this.translate.reloadLang(lang);
    }
  }

  // Syncable
  public async sync(apiLevel: APILevel, syncKey: string): Promise<void> {
    await this.clear();
    await this.loadRemoteTranslations(apiLevel, syncKey);
  }
  public async presync(): Promise<void> {}
  public async postsync(): Promise<void> {}

  // InitializeAware
  async initialize(): Promise<void> {
    this.initializeStatus$.next(InitializeStatus.INITIALIZING);
    try {
      setDefaultOptions({
        weekStartsOn: 1,
      });
      this.initializeStatus$.next(InitializeStatus.INITIALIZED);
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local data: ',
        error?.message
      );
      this.initializeStatus$.next(InitializeStatus.FAILED_INITIALIZE);
    }
  }

  // Private Functions

  private async loadLocalTranslations() {
    try {
      this.logger.log(this.constructor.name, '- Load local Translations');

      let translation = await this.translationsDaoService.getTranslations();

      this.logger.log(
        this.constructor.name,
        'loadLocalTranslations - ',
        Object.keys(translation),
        '- Translationstype:',
        typeof translation
      );

      if (translation != null && translation['imslabels'] != null) {
        await this.applyTranslations(translation['imslabels']);
      }
    } catch (error) {
      this.logger.error(
        this.constructor.name,
        'Error loading local Translations: ',
        error?.message
      );
      throw error;
    }
  }

  private async loadRemoteTranslations(apiLevel: APILevel, syncKey: string) {
    if (apiLevel < APILevel.CUSTOMTRANSLATIONS) {
      this.logger.log(
        this.constructor.name,
        '- Backend API is too low to load custom translations init with none',
        apiLevel,
        APILevel.CUSTOMTRANSLATIONS
      );
      await this.translationsDaoService.saveTranslations({});
      await this.loadLocalTranslations();
      return;
    }

    if (this.authenticationService.authenticationState.getValue()) {
      this.logger.log(
        this.constructor.name,
        '- Load new Translations from IMS'
      );

      try {
        let data: any = await this.remoteActionService.sendRemoteAction(
          new RemoteAction({
            endpoint: 'translations',
            cachable: false,
            fifo: false,
            authenticationRequired: true,
            data: {
              synckey: syncKey
            }
          })
        );

        await this.translationsDaoService.saveTranslations(data);
        await this.loadLocalTranslations();
      } catch (error) {
        this.logger.error(
          this.constructor.name,
          'Error downloading Translations: ',
          error?.message
        );
        throw error;
      }
    }
  }

  async initializeCompleted(): Promise<void> {
    // JB20240626: need to load stored remote translation after all components are loaded,
    // so the internal translater has enough time to load the local translations and they do not override the remotes ones
    await this.loadLocalTranslations();
  }

  private async applyTranslations(translations: Object) {
    if (translations) {
      this.logger.log(
        this.constructor.name,
        'applyTranslations - got localization with keys:',
        Object.keys(translations)
      );

      for (const lang of Object.keys(translations)) {
        this.logger.log(
          this.constructor.name,
          'applyTranslations - merged local translation with localization',
          'for',
          lang
        );
        this.translate.setTranslation(lang, translations[lang], true);
      }
    }
  }
}
